import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useStore } from "@/context/StoreContext";
import { searchApi } from "@/lib/api";
import { Search, X, TrendingUp, Clock } from "lucide-react";
import { img } from "@/lib/img";

export const SearchOverlay = () => {
  const { searchOpen, setSearchOpen } = useStore();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], categories: [], popular: [] });
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lemce_recent_search") || "[]"); } catch { return []; }
  });
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchOpen) setTimeout(() => inputRef.current?.focus(), 100);
    else setQ("");
  }, [searchOpen]);

  useEffect(() => {
    const t = setTimeout(() => {
      searchApi(q).then(setResults).catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const commit = (term) => {
    if (!term.trim()) return;
    const next = [term, ...recent.filter((r) => r !== term)].slice(0, 6);
    setRecent(next);
    localStorage.setItem("lemce_recent_search", JSON.stringify(next));
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent
        className="max-w-2xl bg-lemce-ivory border border-lemce-beige p-0 top-24 translate-y-0 gap-0"
        data-testid="search-overlay"
      >
        <DialogTitle className="sr-only">Search LEMCE</DialogTitle>
        <DialogDescription className="sr-only">Search for premium comfort essentials.</DialogDescription>
        <div className="flex items-center gap-3 px-6 py-5 border-b border-lemce-beige">
          <Search className="w-5 h-5 text-lemce-grey" strokeWidth={1.5} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commit(q)}
            placeholder="Search premium comfort essentials…"
            data-testid="search-input"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-lemce-grey font-sans"
          />
          <button onClick={() => setSearchOpen(false)} aria-label="Close search">
            <X className="w-5 h-5 text-lemce-grey hover:text-lemce-matte" strokeWidth={1.5} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-6">
          {q && results.products.length > 0 && (
            <div className="mb-6">
              <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-3">Products</p>
              <div className="space-y-2">
                {results.products.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/product/${p.slug}`}
                    onClick={() => setSearchOpen(false)}
                    data-testid={`search-result-${p.slug}`}
                    className="flex items-center gap-4 p-2 hover:bg-lemce-beige/50 transition-colors"
                  >
                    <img src={img(p.image, 160)} alt={p.name} loading="lazy" className="w-12 h-14 object-cover bg-lemce-beige" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-lemce-grey">{p.price_display}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {q && results.categories.length > 0 && (
            <div className="mb-6">
              <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-3">Categories</p>
              {results.categories.map((c) => (
                <Link key={c.slug} to={`/collection/${c.slug}`} onClick={() => setSearchOpen(false)}
                  className="block py-1.5 text-sm hover:text-lemce-taupe">{c.title}</Link>
              ))}
            </div>
          )}

          {q && results.products.length === 0 && (
            <p className="text-sm text-lemce-grey py-4">No matches. Try a broader term.</p>
          )}

          {!q && recent.length > 0 && (
            <div className="mb-6">
              <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-3 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Recent
              </p>
              <div className="flex flex-wrap gap-2">
                {recent.map((r) => (
                  <button key={r} onClick={() => commit(r)}
                    className="px-3 py-1.5 border border-lemce-beige text-xs hover:bg-lemce-beige transition-colors">{r}</button>
                ))}
              </div>
            </div>
          )}

          {!q && (
            <div>
              <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-3 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Popular
              </p>
              <div className="flex flex-wrap gap-2">
                {results.popular.map((r) => (
                  <button key={r} onClick={() => commit(r)} data-testid={`popular-search-${r}`}
                    className="px-3 py-1.5 border border-lemce-beige text-xs hover:bg-lemce-beige transition-colors">{r}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchOverlay;
